
export enum UserRole {
  NONE = 'NONE',
  CUSTOMER = 'CUSTOMER',
  WORKER = 'WORKER',
  ADMIN = 'ADMIN'
}

export enum SkillType {
  PLUMBER = 'Plumber',
  ELECTRICIAN = 'Electrician',
  CARPENTER = 'Carpenter',
  MASON = 'Mason',
  PAINTER = 'Painter',
  CLEANER = 'Cleaner',
  MECHANIC = 'Mechanic',
  GARDENER = 'Gardener'
}

export type Language = 'en' | 'hi';
export type Gender = 'Male' | 'Female' | 'Other';

export interface ServiceCategory {
  id: string;
  name: string;
  basePrice: number;
  isActive: boolean;
  parentId?: string; // Link to parent category (null = main category)
}

export interface PlatformFeeConfig {
  baseCommissionPercent: number;
  newWorkerFreeJobsCount: number;
  dynamicMultiplier: number;
  isSystemFreeMode: boolean;
  landingBackgroundType: 'video' | 'image';
  landingVideoUrl: string;
  landingImageUrl: string;
  paymentQrUrl: string;
  upiId: string;
  bankDetails: string;
  googleMapsApiKey?: string;
  subscriptionPrice?: number;
  trialDurationDays?: number;
  maxWorkerRadiusKm?: number; // ✅ Added for configurable radius
}

export type VerificationStatus = 'unverified' | 'pending' | 'verified';
export type JobStatus = 'OPEN' | 'PENDING' | 'ACCEPTED' | 'OTP_VERIFIED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export type AdminPermission = 'dashboard' | 'users' | 'workers' | 'categories' | 'withdrawals' | 'ads' | 'cms' | 'reports' | 'settings' | 'firebase' | 'messages' | 'administrators';

export interface UserProfile {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: Gender | '';
  dob: string;
  avatarUrl: string;
  walletBalance?: number;
  language?: Language;
  status?: 'active' | 'suspended' | 'deleted';
  role?: UserRole;
  adminPermissions?: AdminPermission[];
  current_lat?: number;
  current_lng?: number;
  last_location_update?: string;
}

export interface PortfolioItem {
  id: string;
  type: 'image' | 'video' | 'audio';
  url: string;
  storagePath?: string; // Path in Supabase Storage for deletion
}

export interface Review {
  id: string;
  author: string;
  rating: number;
  text: string;
  date: string;
  mediaUrls?: string[];
  voiceNoteUrl?: string;
}

export interface WorkerProfile {
  id: string;
  phone: string;
  email?: string;
  name: string;
  skill: string;
  tags?: string[];
  rating: number;
  reviewCount: number;
  distanceKm: number;
  hourlyRate: number;
  isOnline: boolean;
  avatarUrl: string;
  coverImageUrl?: string; // ✅ NEW: Cover image for worker profile
  upiId?: string; // ✅ Payment UPI ID
  bio: string;
  portfolioItems?: PortfolioItem[];
  verificationStatus: VerificationStatus;
  idProofUrl?: string;
  coordinates: { x: number; y: number };
  lifetimeJobsCompleted: number;
  totalEarned: number;
  status?: 'active' | 'suspended' | 'deleted' | 'pending';
  subscriptionPlan?: string;
  subscriptionEnd?: string;
  currentJobStatus?: string;
  // ✅ Real GPS coordinates (computed during listenToOnlineWorkers fetch)
  currentLat?: number;
  currentLng?: number;
}

export interface JobRequest {
  id: string;
  workerId?: string;
  workerName?: string;
  workerAvatar?: string;
  workerPhone?: string;
  skill: string;
  title?: string;
  customerId: string;
  customerName?: string; // Optional
  customerAvatar?: string;
  customerPhone?: string;
  status: JobStatus;
  otp: string;
  completionOtp: string;
  completionRequested?: boolean;
  date: string;
  description: string;
  price: number;
  priceSetByWorker?: boolean; // ✅ Added: Worker has set price
  priceAcceptedByCustomer?: boolean; // ✅ Added: Customer has accepted price
  isBroadcast?: boolean;
  mediaUrls?: string[];
  completionMediaUrls?: string[];
  workAddress?: string;
  coordinates?: { x: number; y: number };
  isPaid?: boolean;
  paymentMethod?: 'UPI' | 'CARD' | 'CASH';
  createdAt?: any;
  platformFee?: number;
  workerEarnings?: number;
  voiceNoteUrl?: string;
  cancelledBy?: 'customer' | 'worker';
  userReview?: {
    rating: number;
    text: string;
    mediaUrls?: string[];
    voiceNoteUrl?: string;
  };
}

export interface WithdrawalRequest {
  id: string;
  workerId: string;
  workerName: string;
  amount: number;
  method: 'UPI' | 'BANK';
  details: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedAt: any;
}

export interface Transaction {
  id: string;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  description: string;
  date: string;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  workerId?: string;
}

export interface AdBanner {
  id: string;
  imageUrl: string;
  linkUrl?: string;
  position: 'TOP' | 'BOTTOM';
  isActive: boolean;
  title?: string;
  isUniversal: boolean;
  city?: string;
}

export interface Report {
  id: string;
  reporterId: string;
  reporterName: string;
  reportedId: string;
  reportedName: string;
  reportedRole: UserRole;
  jobId?: string;
  reason: string;
  details: string;
  status: 'PENDING' | 'RESOLVED';
  timestamp: number;
}

export interface EarningsData {
  day: string;
  amount: number;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: string;
}

export interface UserPreferences {
  notifications: boolean;
  theme: 'light' | 'dark';
}
